import {
  nachflussLaeuft,
  TASK_STATUS_LABELS,
  tageOhneBewegung,
  VORHABEN_ZUSTAND_LABELS,
  type VorhabenZustand,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { meilensteineQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { useAufgabenDieserWelt } from '../lib/welt'
import { AufgabeAnlegen } from './AufgabeAnlegen'
import { AufgabenVerweis } from './AufgabeKontext'
import { AufgabeZuordnen } from './AufgabeZuordnen'
import { MeilensteinFormular, terminText } from './MeilensteinFormular'
import { Button } from './ui/primitives'

export type VorhabenEintrag = Awaited<ReturnType<typeof trpc.vorhaben.alle.query>>[number]

/**
 * **Ein Vorhaben ist eine Zeile** (26.08.2026, Entwurf `infos/vorhaben-standalone.html`).
 *
 * ## Warum die Karte weg ist
 *
 * Sie trug Name, drei Marken, Zeitfenster, Zähler, Beschreibungsauszug, Fortschrittsbalken, vier
 * Metazeilen, ein neunfeldriges Formular, den Inhalts-Aufklapper und die Meilensteine mit drei
 * Knöpfen. Aufgeklappt war das ein Bildschirm pro Vorhaben – bei vierzehn Vorhaben sah man drei.
 *
 * Vor allem aber sahen **alle gleich wichtig aus**. „IOR 2.0 · 1/1 · laufend" und „NetCon
 * Construction · 0/0 · Ziel 31.10." standen gleichberechtigt untereinander, obwohl das eine
 * läuft und das andere noch keine einzige Aufgabe hat.
 *
 * ## Die Trennung, die alles trägt
 *
 * | Bewegung | zeigt | wo |
 * |---|---|---|
 * | **Zeile anklicken** | den Inhalt: Meilensteine, offene Aufgaben, Besetzung | hier, aufgeklappt |
 * | **⋯ anklicken** | das eine Vorhaben-Fenster – alle Felder, Wer, Steuerung (seit 23.10.2026) | `VorhabenDetail` |
 * | **Name anklicken** | die Sache selbst: Beschreibung, Diskussion, alle Aufgaben | `VorhabenDetail` |
 *
 * Inhalt und Einstellungen sind zwei Fragen. Sie in dieselbe Fläche zu legen war der Grund,
 * warum die Karte nicht mehr zu überblicken war.
 *
 * ## Warum die Entwicklungsart aus der Zeile fiel
 *
 * „Klassisch / Gemischt / KI-Programmierung" steuert nichts – sie wird angezeigt und sonst
 * nirgends gelesen. In einer Übersicht ist ein Merkmal ohne Wirkung eine Spalte, die man jedes
 * Mal überliest. Sie steht weiter im Detailfenster.
 */
export function VorhabenZeile({
  vorhaben: v,
  zustand,
  jetzt,
  offen,
  umschalten,
  bearbeiten,
  oeffnen,
}: {
  vorhaben: VorhabenEintrag
  zustand: VorhabenZustand
  jetzt: Date
  offen: boolean
  umschalten: () => void
  bearbeiten: () => void
  oeffnen: () => void
}) {
  const anteil = v.stand.gesamt > 0 ? v.stand.erledigt / v.stand.gesamt : 0
  const bis = tageBis(v.zieltermin, jetzt)
  const stillstand = zustand === 'steht' ? tageOhneBewegung(v, jetzt) : null

  /** Mit-Owner zuerst, dann die Wunsch-Entwickler – doppelte Namen nur einmal. */
  const besetzung = [...(v.mitOwnerName ? [v.mitOwnerName] : []), ...v.wunschEntwickler].filter(
    (name, i, alle) => alle.indexOf(name) === i,
  )

  return (
    <>
      {/**
       * **Die ganze Zeile klappt auf**, nicht nur das Dreieck – ein 20-px-Ziel ist am Telefon
       * kein Ziel. Der Name und das ⋯ fangen ihre Klicks selbst ab (`stopPropagation`), weil
       * sie woanders hinführen.
       */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={offen}
        onClick={umschalten}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            umschalten()
          }
        }}
        className="grid cursor-pointer grid-cols-[18px_minmax(0,1fr)_28px] items-center gap-x-3 border-b border-line px-3.5 py-2 last:border-b-0 hover:bg-page lg:grid-cols-[18px_minmax(150px,1.7fr)_74px_98px_minmax(104px,1fr)_88px_72px_66px_28px] lg:py-0 lg:h-[52px]"
      >
        <span
          className={`text-[9px] text-muted transition-transform ${offen ? 'rotate-90' : ''}`}
          aria-hidden="true"
        >
          ▶
        </span>

        <span className="min-w-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              oeffnen()
            }}
            title="Vorhaben öffnen – Beschreibung, Diskussion, Aufgaben"
            className="block max-w-full cursor-pointer truncate text-left text-[13.5px] font-semibold hover:underline"
          >
            {v.label}
          </button>
          {/** Am Rechner die Beschreibung, am Telefon das, was die weggefallenen Spalten sagten. */}
          {vorschautext(v.beschreibung) && (
            <small className="hidden truncate text-[11.5px] text-muted lg:block">
              {vorschautext(v.beschreibung)}
            </small>
          )}
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted lg:hidden">
            <Zustandsmarke zustand={zustand} />
            <span className="tabular">{fortschrittText(v.stand)}</span>
            {v.zieltermin && (
              <span className={`tabular ${bis !== null && bis < 14 ? 'text-pool-sofort' : ''}`}>
                Ziel {datumKurz(v.zieltermin)}
              </span>
            )}
          </span>
        </span>

        <span className="hidden lg:block">
          <span className="rounded-md bg-[#f3f2ef] px-1.5 py-px text-[10px] font-semibold text-ink2">
            {v.typ === 'projekt' ? 'Projekt' : 'Thema'}
          </span>
        </span>

        <span className="hidden lg:block">
          <Zustandsmarke zustand={zustand} tage={stillstand} />
        </span>

        <span className="hidden min-w-0 flex-col gap-1 lg:flex">
          <span className="h-[4px] overflow-hidden rounded-full bg-line">
            <i
              className="block h-full rounded-full bg-accent"
              style={{ width: `${anteil * 100}%` }}
            />
          </span>
          <span className="tabular truncate text-[10.5px] text-muted">
            {fortschrittText(v.stand)}
          </span>
        </span>

        <span className="hidden lg:block">
          <Nachflussmarke vorhaben={v} />
        </span>

        <span
          className={`tabular hidden text-[11px] whitespace-nowrap lg:block ${
            bis !== null && bis < 14 ? 'text-pool-sofort' : 'text-muted'
          }`}
        >
          {v.zieltermin ? datumKurz(v.zieltermin) : 'kein Ziel'}
        </span>

        <span className="hidden lg:flex">
          {besetzung.slice(0, 3).map((name) => (
            <Kopf key={name} name={name} />
          ))}
          {besetzung.length > 3 && <Kopf name={`+${besetzung.length - 3}`} blass />}
        </span>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            bearbeiten()
          }}
          title="Vorhaben öffnen – alle Felder, Wer, Termine, Steuerung"
          aria-label={`${v.label} bearbeiten`}
          className="cursor-pointer text-center text-muted hover:text-ink"
        >
          ⋯
        </button>
      </div>

      {offen && (
        <div className="border-b border-line bg-page px-3.5 py-3 last:border-b-0 lg:pl-[52px]">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Meilensteine vorhabenId={v.id} />
            <OffeneAufgaben vorhabenId={v.id} />
            <Besetzung vorhaben={v} />
          </div>
          <Aktionen vorhaben={v} oeffnen={oeffnen} />
        </div>
      )}
    </>
  )
}

/* ─────────────────────────── Marken in der Zeile ──────────────────────────── */

/**
 * **Der Zustand ist die einzige Farbe in der Zeile** – und steht nie allein als Farbe da: das
 * Wort steht daneben. Fünf Farbpunkte ohne Beschriftung wären für Farbfehlsichtige fünfmal
 * derselbe Punkt (dieselbe Zusage wie bei den Pool-Farben, AP 2.3).
 */
const ZUSTAND_STIL: Record<VorhabenZustand, { punkt: string; text: string }> = {
  trocken: { punkt: 'bg-warn', text: 'text-warn' },
  steht: { punkt: 'bg-pool-sofort', text: 'text-pool-sofort' },
  leer: { punkt: 'bg-pool-projekte', text: 'text-pool-projekte' },
  zieht: { punkt: 'bg-good', text: 'text-good-text' },
  ruhig: { punkt: 'bg-muted', text: 'text-muted' },
  fertig: { punkt: 'bg-muted', text: 'text-muted' },
}

export function Zustandsmarke({
  zustand,
  tage,
}: {
  zustand: VorhabenZustand
  /** Nur bei „steht": *seit wann*. Ohne die Zahl ist der Zustand eine Behauptung. */
  tage?: number | null
}) {
  const stil = ZUSTAND_STIL[zustand]
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${stil.text}`}>
      <i className={`size-[6px] shrink-0 rounded-full ${stil.punkt}`} aria-hidden="true" />
      {VORHABEN_ZUSTAND_LABELS[zustand]}
      {typeof tage === 'number' && <span className="tabular font-normal">{tage} T</span>}
    </span>
  )
}

/**
 * **Fünf Striche statt „0 / 10"** (Entwurf). Man sieht beim Überfliegen, wie viele Vorhaben
 * trocken laufen, ohne jede Zahl einzeln zu lesen.
 *
 * Die Striche zeigen den **Bestand im Pool**, nicht das Limit – wie voll es gerade ist.
 *
 * ## Drei Fälle, zwei Farben
 *
 * | Lage | Anzeige |
 * |---|---|
 * | Nachfluss läuft | Striche und Zahl, grau |
 * | Nachfluss gewollt, aber kein Pool oder keine Grenze | **„kein Nachfluss", Warnfarbe** – ein Befund |
 * | Nachfluss abgewählt | „von Hand", grau – eine Einstellung |
 *
 * Der Unterschied zwischen den letzten beiden ist der ganze Punkt von
 * [E-256](../../../../docs/ENTSCHEIDUNGEN.md#e-256): dieselbe Wirkung, entgegengesetzte
 * Bedeutung. Nur der eine Fall ist ein Fehler, und nur er bekommt Farbe.
 */
function Nachflussmarke({ vorhaben: v }: { vorhaben: VorhabenEintrag }) {
  if (!v.nachflussGewuenscht) {
    return (
      <span
        className="text-[11px] text-muted"
        title="Nachfluss nicht gewünscht – die Aufgaben holt sich jemand von Hand"
      >
        von Hand
      </span>
    )
  }
  if (!nachflussLaeuft(v)) {
    return (
      <span
        className="text-[11px] text-warn"
        title="Kein Pool oder keine Grenze – es fließt nichts"
      >
        kein Nachfluss
      </span>
    )
  }
  const voll = Math.min(v.stand.imPool, 5)
  return (
    <span
      className="tabular flex items-center gap-1.5 text-[11px] text-muted"
      title={`${v.stand.imPool} von höchstens ${v.nachflussLimit} im Pool`}
    >
      <span className="flex gap-px" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <i key={i} className={`h-3 w-[3px] rounded-[1px] ${i < voll ? 'bg-good' : 'bg-line2'}`} />
        ))}
      </span>
      {v.stand.imPool}/{v.nachflussLimit}
    </span>
  )
}

function Kopf({ name, blass = false }: { name: string; blass?: boolean }) {
  return (
    <span
      title={name}
      className={`tabular -ml-1.5 grid size-[21px] shrink-0 place-items-center rounded-full border-2 border-surface text-[8.5px] font-bold first:ml-0 ${
        blass ? 'bg-line text-muted' : 'bg-vorhaben-bg text-pool-projekte'
      }`}
    >
      {blass ? name : initialen(name)}
    </span>
  )
}

/* ─────────────────────────── Der Aufklappbereich ──────────────────────────── */

function Spalte({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <h5 className="mb-1.5 text-[9.5px] font-normal tracking-[0.1em] text-muted uppercase">
        {titel}
      </h5>
      {children}
    </div>
  )
}

/**
 * **Meilensteine eines Vorhabens** (Phase 8).
 *
 * Nils, 04.08.2026: *„Meilensteine können von mehreren Aufgaben bedient werden, müssen aber
 * nicht."* Deshalb steht an jedem, **wie viele Aufgaben darauf zeigen** – und null ist ein
 * gültiger Wert: *„Abnahme beim Kunden"* steht im Kalender, auch wenn dafür niemand etwas
 * programmiert.
 *
 * **Erreicht wird von Hand gesetzt.** Ein Meilenstein mit erledigten Aufgaben ist nicht
 * automatisch erreicht, und einer ohne Aufgaben ließe sich sonst nie erreichen.
 *
 * **Ändern geht hier genauso wie im Detail** (28.08.2026,
 * [E-273](../../../../docs/ENTSCHEIDUNGEN.md#e-273)): ein Klick auf den Namen macht aus der
 * Zeile dasselbe Formular, mit dem sie angelegt würde – Termin darf dabei leer bleiben.
 */
function Meilensteine({ vorhabenId }: { vorhabenId: string }) {
  const queryClient = useQueryClient()
  const { data: alle = [] } = useQuery(meilensteineQuery)
  const meine = alle.filter((m) => m.vorhabenId === vorhabenId)
  const [offen, setOffen] = useState(false)
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)

  const neuLaden = () => queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
  const anlegen = useMutation({
    mutationFn: (m: { label: string; termin: string | null }) =>
      trpc.vorhaben.meilensteinAnlegen.mutate({ vorhabenId, ...m }),
    onSuccess: async () => {
      setOffen(false)
      await neuLaden()
    },
  })
  const aendern = useMutation({
    mutationFn: (m: { id: string; label: string; termin: string | null }) =>
      trpc.vorhaben.meilensteinAendern.mutate(m),
    onSuccess: async () => {
      setBearbeitet(null)
      await neuLaden()
    },
  })
  const erreicht = useMutation({
    mutationFn: (m: { id: string; erreicht: boolean }) =>
      trpc.vorhaben.meilensteinErreicht.mutate(m),
    onSuccess: neuLaden,
  })
  const entfernen = useMutation({
    mutationFn: (id: string) => trpc.vorhaben.meilensteinEntfernen.mutate({ id }),
    onSuccess: async () => {
      setBearbeitet(null)
      await neuLaden()
    },
  })

  return (
    <Spalte titel="Meilensteine">
      {meine.length === 0 && !offen && <p className="text-[12px] text-muted">Keine.</p>}
      {meine.map((m) =>
        bearbeitet === m.id ? (
          <MeilensteinFormular
            key={m.id}
            label={m.label}
            termin={m.termin}
            laeuft={aendern.isPending || entfernen.isPending}
            aktion="Speichern"
            schmal
            onSpeichern={(werte) => aendern.mutate({ id: m.id, ...werte })}
            onAbbrechen={() => setBearbeitet(null)}
            onEntfernen={() => entfernen.mutate(m.id)}
          />
        ) : (
          <div key={m.id} className="flex items-center gap-2 py-[3px] text-[12.5px]">
            <input
              type="checkbox"
              checked={m.erreichtAm !== null}
              onChange={(e) => erreicht.mutate({ id: m.id, erreicht: e.target.checked })}
              aria-label={`${m.label} erreicht`}
              className="cursor-pointer"
            />
            <button
              type="button"
              onClick={() => setBearbeitet(m.id)}
              title="Meilenstein ändern"
              className={`min-w-0 cursor-pointer truncate text-left hover:underline ${
                m.erreichtAm ? 'text-muted line-through' : ''
              }`}
            >
              {m.label}
            </button>
            <span className="tabular ml-auto shrink-0 text-[10.5px] text-muted">
              {terminText(m.termin)}
            </span>
          </div>
        ),
      )}

      {offen && (
        <MeilensteinFormular
          label=""
          termin={null}
          laeuft={anlegen.isPending}
          aktion="Hinzufügen"
          schmal
          onSpeichern={(werte) => anlegen.mutate(werte)}
          onAbbrechen={() => setOffen(false)}
        />
      )}

      {!offen && (
        <button
          type="button"
          onClick={() => setOffen(true)}
          className="mt-1 cursor-pointer text-[11.5px] text-accent-deep hover:underline"
        >
          + Meilenstein
        </button>
      )}
    </Spalte>
  )
}

/**
 * **Was in einem Vorhaben steckt** (Nils, 17.08.2026: *„sehe auch nicht, was da alles drin
 * ist"*).
 *
 * Nur die **offenen**, und höchstens fünf: das Aufklappen soll zeigen, woran gerade gearbeitet
 * wird, nicht die Vollständigkeit herstellen. Wer alles will, öffnet das Vorhaben – der Weg
 * dorthin steht direkt darunter.
 */
function OffeneAufgaben({ vorhabenId }: { vorhabenId: string }) {
  /** Vorhaben sind an Arbeitswelten gebunden – ihre Aufgaben also auch (Z-07). */
  const { aufgaben } = useAufgabenDieserWelt()

  const meine = aufgaben.filter((a) => a.vorhabenId === vorhabenId)
  const offene = meine.filter((a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt')

  return (
    <Spalte titel={`Offene Aufgaben (${offene.length})`}>
      {offene.length === 0 && <p className="text-[12px] text-muted">Keine offenen.</p>}
      {offene.slice(0, 5).map((a) => (
        <div key={a.id} className="flex items-center gap-2 py-[3px] text-[12.5px]">
          <i className="size-[5px] shrink-0 rounded-full bg-line2" aria-hidden="true" />
          <AufgabenVerweis
            aufgabeId={a.id}
            title={a.title}
            className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
          >
            {a.title}
          </AufgabenVerweis>
          <span className="shrink-0 text-[10.5px] text-muted">
            {TASK_STATUS_LABELS[a.status] ?? a.status}
          </span>
        </div>
      ))}
      {offene.length > 5 && (
        <p className="mt-1 text-[11px] text-muted">und {offene.length - 5} weitere</p>
      )}
    </Spalte>
  )
}

/** Die Namen im Klartext – in der Zeile stehen nur Initialen. */
function Besetzung({ vorhaben: v }: { vorhaben: VorhabenEintrag }) {
  return (
    <Spalte titel="Besetzung">
      <Angabe label="Mit-Owner" wert={v.mitOwnerName ?? '–'} />
      <Angabe label="Wunsch" wert={v.wunschEntwickler.join(', ') || '–'} />
      <Angabe
        label="Pool"
        wert={v.poolLabel ? `${v.poolIcon ?? ''} ${v.poolLabel}` : '– nicht angebunden'}
      />
      <Angabe
        label="Nachfluss"
        wert={
          nachflussLaeuft(v)
            ? `${v.stand.imPool} von höchstens ${v.nachflussLimit}`
            : v.nachflussGewuenscht
              ? 'nichts eingestellt'
              : 'nicht gewünscht – von Hand'
        }
      />
    </Spalte>
  )
}

function Angabe({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex gap-2 py-[3px] text-[12.5px]">
      <span className="w-[68px] shrink-0 text-muted">{label}</span>
      <span className="min-w-0 flex-1 break-words">{wert}</span>
    </div>
  )
}

/**
 * ## Die drei Knöpfe stehen zusammen (21.08.2026)
 *
 * Nils: *„über Vorhaben auch neben + Meilenstein → + Aufgabe und + Aufgabe zuweisen (aus
 * bestehenden)"*. Sie gehören in eine Zeile, weil sie dieselbe Frage beantworten – *was kommt
 * in dieses Vorhaben hinein?* –, und die Unterscheidung ist keine Spitzfindigkeit:
 * **anlegen** heißt, die Arbeit gibt es noch nicht; **zuweisen** heißt, sie liegt im Bestand.
 * Ein einzelner Knopf „+ Aufgabe" hätte den zweiten Fall verschluckt, und der ist bei 150
 * offenen Aufgaben der häufigere.
 */
function Aktionen({ vorhaben: v, oeffnen }: { vorhaben: VorhabenEintrag; oeffnen: () => void }) {
  const [aufgabeOffen, setAufgabeOffen] = useState(false)
  const [zuordnenOffen, setZuordnenOffen] = useState(false)

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => setAufgabeOffen(true)}>
          + Aufgabe
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setZuordnenOffen(true)}>
          + Aufgabe zuweisen
        </Button>
        <Button variant="ghost" size="sm" onClick={oeffnen}>
          Vorhaben öffnen
        </Button>
      </div>

      {aufgabeOffen && (
        <AufgabeAnlegen
          schliessen={() => setAufgabeOffen(false)}
          vorbelegt={{ vorhabenId: v.id }}
        />
      )}
      {zuordnenOffen && (
        <AufgabeZuordnen
          vorhabenId={v.id}
          vorhabenLabel={v.label}
          schliessen={() => setZuordnenOffen(false)}
        />
      )}
    </>
  )
}

/* ─────────────────────────── Kleinkram ────────────────────────────────────── */

function fortschrittText(stand: { gesamt: number; erledigt: number }): string {
  /** **Nicht „0 %".** Ein Vorhaben ohne Aufgaben ist nicht zu null Prozent fertig, es ist leer. */
  if (stand.gesamt === 0) return 'noch keine Aufgaben'
  return `${stand.erledigt} von ${stand.gesamt} Aufgaben`
}

/** Tage bis zum Zieltermin; `null`, wenn keiner gesetzt ist. Negativ heißt überfällig. */
function tageBis(zieltermin: Date | string | null, jetzt: Date): number | null {
  if (!zieltermin) return null
  const ziel = new Date(zieltermin)
  if (Number.isNaN(ziel.getTime())) return null
  return Math.round((ziel.getTime() - jetzt.getTime()) / 86_400_000)
}

function datumKurz(zieltermin: Date | string): string {
  return new Date(zieltermin).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function initialen(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * **Die Beschreibung als ein Satz für die Zeile** (21.08.2026).
 *
 * Bewusst grob: Verweise auf Anhänge fallen weg, Auszeichnungszeichen auch, Zeilenumbrüche
 * werden zu Leerzeichen. Das ist **kein Markdown-Parser** und soll keiner sein – ein Auszug
 * muss nur lesbar sein, und wer es genau wissen will, öffnet das Vorhaben.
 *
 * Kürzer als früher (180 → 120 Zeichen): die Zeile schneidet ohnehin bei einer Zeile ab, und
 * ein Text, den niemand je ganz sieht, kostet nur Rechenzeit beim Zeichnen.
 */
export function vorschautext(beschreibung: string | null): string {
  if (!beschreibung) return ''
  const nackt = beschreibung
    /** Bild und Datei aus der Ablage – der Verweis selbst sagt einem Menschen nichts. */
    .replace(/!?\[([^\]\n]*)\]\(anhang:[^)]*\)/g, '$1')
    /** Übrige Links: der Text zählt, die Adresse nicht. */
    .replace(/\[([^\]\n]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}[-*+]\s+/gm, '')
    .replace(/[*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (nackt.length <= 120) return nackt
  const kurz = nackt.slice(0, 120)
  const luecke = kurz.lastIndexOf(' ')
  return `${luecke > 80 ? kurz.slice(0, luecke) : kurz}…`
}
